import type { ProjectPlan, TeamInfo, StudentInfo, WeeklyProgress, ScoreItem } from '@/lib/index';

export const projectPlanData: ProjectPlan = {
  id: 'polytech-smartfactory-2026',
  title: '폴리텍 대학 스마트팩토리과 프로젝트 관리 시스템',
  description: '학생 프로젝트 진도 관리 및 평가를 위한 통합 관리 시스템',
  department: '스마트팩토리과',
  year: 2026,
  semester: 1,
  startDate: '2026-03-02',
  endDate: '2026-06-20',
  totalWeeks: 16,
};

export const systemRequirements = {
  functional: [
    {
      id: 'FR-001',
      category: '사용자 관리',
      requirement: '교수와 학생 역할별 권한 구분',
      priority: 'high',
      description: '교수는 전체 데이터 조회/수정 권한, 학생은 본인 팀 데이터만 조회 가능',
    },
    {
      id: 'FR-002',
      category: '학생 정보 관리',
      requirement: '학생 프로필 등록 및 관리',
      priority: 'high',
      description: '학번, 이름, 사진, 소속 팀 정보 등록 및 수정',
    },
    {
      id: 'FR-003',
      category: '팀 관리',
      requirement: '조별 구성 및 프로젝트 유형 관리',
      priority: 'high',
      description: '1학년 AI 프로젝트 4개조, 2학년 한이음 3개조 구성 및 관리',
    },
    {
      id: 'FR-004',
      category: '학기 관리',
      requirement: '학기별 일정 및 주차 관리',
      priority: 'high',
      description: '2026년 1학기/2학기 각 16주 일정 관리',
    },
    {
      id: 'FR-005',
      category: '진도 관리',
      requirement: '주차별 진도 체크 및 결과 등록',
      priority: 'high',
      description: '매주 팀별 진도 상태(완료/진행중/대기) 및 상세 내용 기록',
    },
    {
      id: 'FR-006',
      category: '평가 관리',
      requirement: '다양한 평가 항목별 점수 관리',
      priority: 'high',
      description: '출석, 과제, 발표, 참여도, 팀 기여도 등 5개 항목 점수 입력 및 집계',
    },
    {
      id: 'FR-007',
      category: '대시보드',
      requirement: '조별 진도 현황 시각화',
      priority: 'medium',
      description: '팀별 주차별 진도율을 차트로 표시',
    },
    {
      id: 'FR-008',
      category: '대시보드',
      requirement: '학생별 누적 점수 현황',
      priority: 'medium',
      description: '학생별 평가 항목별 점수 및 총점 표시',
    },
  ],
  nonFunctional: [
    {
      id: 'NFR-001',
      category: '성능',
      requirement: '페이지 로딩 시간 3초 이내',
      priority: 'medium',
    },
    {
      id: 'NFR-002',
      category: '보안',
      requirement: '사용자 인증 및 권한 검증',
      priority: 'high',
    },
    {
      id: 'NFR-003',
      category: '사용성',
      requirement: '직관적인 UI/UX 설계',
      priority: 'high',
    },
    {
      id: 'NFR-004',
      category: '호환성',
      requirement: '주요 브라우저(Chrome, Edge, Safari) 지원',
      priority: 'medium',
    },
    {
      id: 'NFR-005',
      category: '반응형',
      requirement: '데스크톱 및 태블릿 화면 지원',
      priority: 'medium',
    },
  ],
  userRequirements: [
    {
      id: 'UR-001',
      role: '교수',
      requirement: '전체 학생 및 팀 현황을 한눈에 파악',
      description: '대시보드를 통해 모든 팀의 진도와 학생 평가 현황 조회',
    },
    {
      id: 'UR-002',
      role: '교수',
      requirement: '주차별 진도 및 평가 점수 입력',
      description: '매주 팀별 진도 상태 업데이트 및 학생별 평가 점수 입력',
    },
    {
      id: 'UR-003',
      role: '학생',
      requirement: '본인 팀의 진도 현황 확인',
      description: '소속 팀의 주차별 진도 및 제출 내역 조회',
    },
    {
      id: 'UR-004',
      role: '학생',
      requirement: '본인의 평가 점수 확인',
      description: '출석, 과제, 발표 등 항목별 점수 및 총점 조회',
    },
  ],
};

export const developmentSchedule = {
  phases: [
    {
      id: 'phase-1',
      name: '요구사항 분석 및 설계',
      startWeek: 1,
      endWeek: 2,
      duration: '2주',
      tasks: [
        '사용자 요구사항 수집 및 분석',
        '시스템 기능 명세서 작성',
        '데이터베이스 스키마 설계',
        'UI/UX 와이어프레임 설계',
      ],
      deliverables: ['요구사항 명세서', '시스템 설계서', 'DB 스키마', 'UI 와이어프레임'],
    },
    {
      id: 'phase-2',
      name: '프론트엔드 개발',
      startWeek: 3,
      endWeek: 6,
      duration: '4주',
      tasks: [
        'React 프로젝트 초기 설정',
        '공통 컴포넌트 개발 (Layout, Navigation)',
        '대시보드 페이지 개발',
        '학생/팀 관리 페이지 개발',
        '진도 관리 페이지 개발',
        '평가 관리 페이지 개발',
      ],
      deliverables: ['프론트엔드 UI 컴포넌트', '페이지별 화면 구현'],
    },
    {
      id: 'phase-3',
      name: '백엔드 개발',
      startWeek: 5,
      endWeek: 8,
      duration: '4주',
      tasks: [
        'Supabase 프로젝트 설정',
        '데이터베이스 테이블 생성',
        '인증 및 권한 관리 구현',
        'API 엔드포인트 개발',
        '데이터 CRUD 로직 구현',
      ],
      deliverables: ['데이터베이스 구축', 'API 서버', '인증 시스템'],
    },
    {
      id: 'phase-4',
      name: '통합 및 테스트',
      startWeek: 9,
      endWeek: 11,
      duration: '3주',
      tasks: [
        '프론트엔드-백엔드 연동',
        '기능별 단위 테스트',
        '통합 테스트',
        '사용자 시나리오 테스트',
        '버그 수정 및 최적화',
      ],
      deliverables: ['통합 시스템', '테스트 보고서'],
    },
    {
      id: 'phase-5',
      name: '배포 및 운영',
      startWeek: 12,
      endWeek: 13,
      duration: '2주',
      tasks: [
        '프로덕션 환경 설정',
        '시스템 배포',
        '사용자 교육 및 매뉴얼 작성',
        '초기 운영 모니터링',
      ],
      deliverables: ['배포된 시스템', '사용자 매뉴얼', '운영 가이드'],
    },
    {
      id: 'phase-6',
      name: '유지보수 및 개선',
      startWeek: 14,
      endWeek: 16,
      duration: '3주',
      tasks: [
        '사용자 피드백 수집',
        '기능 개선 및 추가',
        '성능 최적화',
        '최종 문서화',
      ],
      deliverables: ['개선된 시스템', '최종 프로젝트 보고서'],
    },
  ],
  milestones: [
    {
      id: 'milestone-1',
      name: '설계 완료',
      week: 2,
      date: '2026-03-13',
      description: '시스템 설계 및 기획 완료',
    },
    {
      id: 'milestone-2',
      name: 'UI 개발 완료',
      week: 6,
      date: '2026-04-10',
      description: '모든 화면 UI 구현 완료',
    },
    {
      id: 'milestone-3',
      name: '백엔드 개발 완료',
      week: 8,
      date: '2026-04-24',
      description: 'API 및 데이터베이스 구축 완료',
    },
    {
      id: 'milestone-4',
      name: '통합 테스트 완료',
      week: 11,
      date: '2026-05-15',
      description: '전체 시스템 통합 및 테스트 완료',
    },
    {
      id: 'milestone-5',
      name: '시스템 배포',
      week: 13,
      date: '2026-05-29',
      description: '프로덕션 환경 배포 완료',
    },
    {
      id: 'milestone-6',
      name: '프로젝트 완료',
      week: 16,
      date: '2026-06-20',
      description: '최종 프로젝트 완료 및 인계',
    },
  ],
  techStack: {
    frontend: [
      { name: 'React 18', description: 'UI 라이브러리' },
      { name: 'TypeScript', description: '타입 안정성' },
      { name: 'Tailwind CSS', description: '스타일링' },
      { name: 'shadcn/ui', description: 'UI 컴포넌트' },
      { name: 'React Router', description: '라우팅' },
      { name: 'React Query', description: '데이터 페칭' },
      { name: 'Recharts', description: '차트 시각화' },
    ],
    backend: [
      { name: 'Supabase', description: 'BaaS 플랫폼' },
      { name: 'PostgreSQL', description: '데이터베이스' },
      { name: 'Supabase Auth', description: '인증 시스템' },
      { name: 'Supabase Storage', description: '파일 저장소' },
    ],
    tools: [
      { name: 'Vite', description: '빌드 도구' },
      { name: 'ESLint', description: '코드 품질' },
      { name: 'Git', description: '버전 관리' },
      { name: 'Vercel', description: '배포 플랫폼' },
    ],
  },
};

export const systemArchitecture = {
  overview: '3-Tier 웹 애플리케이션 아키텍처',
  layers: [
    {
      name: 'Presentation Layer',
      description: 'React 기반 사용자 인터페이스',
      components: ['대시보드', '학생 관리', '팀 관리', '진도 관리', '평가 관리'],
    },
    {
      name: 'Application Layer',
      description: '비즈니스 로직 및 상태 관리',
      components: ['React Query', 'Zustand', 'Custom Hooks', 'API Client'],
    },
    {
      name: 'Data Layer',
      description: 'Supabase 백엔드 서비스',
      components: ['PostgreSQL', 'Authentication', 'Storage', 'Real-time'],
    },
  ],
  database: {
    tables: [
      {
        name: 'users',
        description: '사용자 정보',
        columns: ['id', 'email', 'role', 'name', 'created_at'],
      },
      {
        name: 'students',
        description: '학생 정보',
        columns: ['id', 'student_number', 'name', 'photo_url', 'team_id', 'user_id'],
      },
      {
        name: 'teams',
        description: '팀 정보',
        columns: ['id', 'name', 'grade', 'project_type', 'mentor', 'created_at'],
      },
      {
        name: 'weekly_progress',
        description: '주차별 진도',
        columns: ['id', 'team_id', 'week', 'date', 'status', 'description', 'submitted_by', 'submitted_at'],
      },
      {
        name: 'scores',
        description: '평가 점수',
        columns: ['id', 'student_id', 'category', 'score', 'max_score', 'week', 'date', 'notes'],
      },
    ],
  },
};

export const sampleTeams: TeamInfo[] = [
  {
    id: 'team-1-1',
    name: '1학년 1조',
    grade: 1,
    members: [],
    projectType: 'ai',
  },
  {
    id: 'team-1-2',
    name: '1학년 2조',
    grade: 1,
    members: [],
    projectType: 'ai',
  },
  {
    id: 'team-1-3',
    name: '1학년 3조',
    grade: 1,
    members: [],
    projectType: 'ai',
  },
  {
    id: 'team-1-4',
    name: '1학년 4조',
    grade: 1,
    members: [],
    projectType: 'ai',
  },
  {
    id: 'team-2-1',
    name: '2학년 1조',
    grade: 2,
    members: [],
    projectType: 'hanium',
    mentor: '김멘토 교수',
  },
  {
    id: 'team-2-2',
    name: '2학년 2조',
    grade: 2,
    members: [],
    projectType: 'hanium',
    mentor: '이멘토 교수',
  },
  {
    id: 'team-2-3',
    name: '2학년 3조',
    grade: 2,
    members: [],
    projectType: 'hanium',
    mentor: '박멘토 교수',
  },
];
